// Admin — Monthly Theme orchestrator.
//
// One-click editorial pipeline powered by DeepSeek (see
// backend/routes/admin_monthly_theme.py).
//
// Flow:
//  1. Admin types a theme + toggles what to include + optionally binds a
//     Gelato print product.
//  2. Backend enqueues a background task, we poll /:bundle_id every 2 s.
//  3. Once status="done", the drafts appear in expandable panels; the
//     admin can review then publish (creates real blog articles, cépages
//     records, producer leads and Academy course meta).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Sparkles, Play, CheckCircle2, XCircle, ChevronDown, ChevronRight, Trash2, Upload, RefreshCw, Edit3, Save, X } from "lucide-react";

const AUTH_KEY = "geovinum.auth.v1";

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const t = raw ? JSON.parse(raw)?.token : null;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

const api = (path, opts = {}) =>
  fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...authHeaders(), ...(opts.headers || {}) },
  }).then(async (r) => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
    return data;
  });

const AdminMonthlyThemePage = () => {
  const [theme, setTheme] = useState("");
  const [settings, setSettings] = useState({
    include_blog: true,
    include_academy: true,
    include_producers: true,
    include_translations: true,
    include_newsletter: true,
    include_podcast: true,
    include_icp_prospecting: true,
    print_product_id: "",
  });
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [publishModal, setPublishModal] = useState(null); // { bid, sendAt, sendNewsletter }
  const pollRef = useRef(null);

  const refreshList = useCallback(async () => {
    try {
      const r = await api("/api/admin/monthly-theme?limit=20");
      setBundles(r.items || []);
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => {
    refreshList();
    // Preload Gelato products for the "bind print" selector
    api("/api/shop/products?limit=100").then((r) => setProducts(r.items || r.products || [])).catch(() => {});
  }, [refreshList]);

  // Poll the selected bundle while running
  useEffect(() => {
    if (!selected?.bundle_id) return undefined;
    if (["done", "failed", "published"].includes(selected.status)) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const b = await api(`/api/admin/monthly-theme/${selected.bundle_id}`);
        setSelected(b);
        setBundles((prev) => prev.map((x) => (x.bundle_id === b.bundle_id ? b : x)));
      } catch {
        // Silently ignore transient polling errors
      }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [selected?.bundle_id, selected?.status]);

  const generate = async () => {
    setError(""); setBusy(true);
    try {
      const payload = {
        theme: theme.trim(),
        ...settings,
        print_product_id: settings.print_product_id || null,
      };
      const r = await api("/api/admin/monthly-theme/generate", { method: "POST", body: JSON.stringify(payload) });
      const b = await api(`/api/admin/monthly-theme/${r.bundle_id}`);
      setSelected(b);
      setBundles((prev) => [b, ...prev]);
      setTheme("");
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const publish = async (bid) => {
    // Open the publish modal — picks the newsletter send datetime.
    // Default: now + 1h, local timezone, truncated to the next minute.
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setPublishModal({ bid, sendAt: local, sendNewsletter: true });
  };

  const confirmPublish = async () => {
    if (!publishModal) return;
    const { bid, sendAt, sendNewsletter } = publishModal;
    try {
      const body = {
        send_newsletter: sendNewsletter,
        send_at: sendNewsletter ? new Date(sendAt).toISOString() : null,
      };
      const r = await api(`/api/admin/monthly-theme/${bid}/publish`, { method: "POST", body: JSON.stringify(body) });
      setPublishModal(null);
      window.alert(`Publié : ${JSON.stringify(r.published)}${r.newsletter_send_at ? `\nNewsletter programmée le ${new Date(r.newsletter_send_at).toLocaleString()}` : ""}`);
      const b = await api(`/api/admin/monthly-theme/${bid}`);
      setSelected(b); refreshList();
    } catch (e) { setError(e.message); setPublishModal(null); }
  };

  const del = async (bid) => {
    if (!window.confirm("Supprimer ce bundle ?")) return;
    try {
      await api(`/api/admin/monthly-theme/${bid}`, { method: "DELETE" });
      if (selected?.bundle_id === bid) setSelected(null);
      refreshList();
    } catch (e) { setError(e.message); }
  };

  // Element-level regeneration + manual edit on an existing (even published) bundle.
  const [regenBusy, setRegenBusy] = useState(null);  // element key
  const [editingEl, setEditingEl] = useState(null);  // {element, text}
  const [editSaving, setEditSaving] = useState(false);
  const [newsletterLangModal, setNewsletterLangModal] = useState(null);  // {langs:Set}

  const regenElement = async (element, extra = null) => {
    if (!selected?.bundle_id) return;
    setRegenBusy(element); setError("");
    try {
      const body = { element };
      if (extra) Object.assign(body, extra);
      await api(`/api/admin/monthly-theme/${selected.bundle_id}/regenerate`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      // Poll until regen_running flips false
      for (let i = 0; i < 90; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        const b = await api(`/api/admin/monthly-theme/${selected.bundle_id}`);
        setSelected(b);
        setBundles((prev) => prev.map((x) => (x.bundle_id === b.bundle_id ? b : x)));
        if (b.regen_running === false || b.regen_running === undefined) break;
      }
    } catch (e) { setError(e.message); }
    finally { setRegenBusy(null); }
  };

  const openEdit = (element) => {
    const val = selected?.[element];
    setEditingEl({ element, text: JSON.stringify(val ?? {}, null, 2) });
  };

  const saveEdit = async () => {
    if (!editingEl) return;
    setEditSaving(true); setError("");
    try {
      const parsed = JSON.parse(editingEl.text);
      const r = await api(`/api/admin/monthly-theme/${selected.bundle_id}/element`, {
        method: "PATCH",
        body: JSON.stringify({ element: editingEl.element, value: parsed }),
      });
      setSelected(r.bundle);
      setBundles((prev) => prev.map((x) => (x.bundle_id === r.bundle.bundle_id ? r.bundle : x)));
      setEditingEl(null);
    } catch (e) {
      setError(e.message?.includes("JSON") ? `JSON invalide : ${e.message}` : e.message);
    } finally { setEditSaving(false); }
  };

  const progressPct = useMemo(() => {
    if (!selected || !selected.total_steps) return 0;
    return Math.round((selected.progress / selected.total_steps) * 100);
  }, [selected]);

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="admin-monthly-theme">
      <div className="flex items-center gap-3 mb-6">
        <Sparkles size={26} className="text-[color:var(--gv-pink)]" />
        <h1 className="text-3xl font-serif">Thème du mois · DeepSeek</h1>
      </div>

      <p className="text-[13.5px] text-[color:var(--gv-ink)]/60 mb-6 max-w-2xl">
        Tape un thème viticole (ex. <em>« Crémant de Bourgogne »</em>) et DeepSeek génère en un clic :
        fiche appellation, cépages manquants, producteurs emblématiques, articles de blog, cours Academy
        et traductions FR/EN/ES/PT. Tout est enregistré en brouillon — tu revois, puis tu publies.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px] text-[13px] mb-4">{error}</div>
      )}

      {/* GENERATION FORM */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-5 mb-8 shadow-sm">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1.5">Thème</label>
            <input
              type="text"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="Ex. Crémant de Bourgogne, Vin Jaune du Jura, Chianti Classico…"
              className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2.5 text-[14px] focus:border-[color:var(--gv-pink)] focus:outline-none"
              data-testid="theme-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1.5">Carte à associer (option)</label>
            <select
              value={settings.print_product_id}
              onChange={(e) => setSettings({ ...settings, print_product_id: e.target.value })}
              className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2.5 text-[14px] focus:border-[color:var(--gv-pink)] focus:outline-none"
              data-testid="print-product-select"
            >
              <option value="">— Aucune carte —</option>
              {products.map((p) => (
                <option key={p.product_id || p.id} value={p.product_id || p.id}>{p.title || p.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mt-5 mb-5 text-[13px]">
          {[
            ["include_producers", "Producteurs"],
            ["include_blog", "Articles blog"],
            ["include_academy", "Cours Academy"],
            ["include_newsletter", "Newsletter 4 langues"],
            ["include_podcast", "Script podcast 4 langues"],
            ["include_icp_prospecting", "Prospection B2B (9 ICP × 4 produits)"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings[k] !== false}
                onChange={(e) => setSettings({ ...settings, [k]: e.target.checked })}
                className="accent-[color:var(--gv-pink)]"
                data-testid={`toggle-${k}`}
              />
              {label}
            </label>
          ))}
          <div className="flex items-center gap-2 text-[color:var(--gv-ink)]/55">
            <span className="inline-block w-3 h-3 rounded-full bg-[color:var(--gv-pink)]/60" /> Traductions FR/EN/ES/PT (toujours actives)
          </div>
        </div>

        <button
          onClick={generate}
          disabled={!theme.trim() || busy}
          className="inline-flex items-center gap-2 bg-[color:var(--gv-pink)] hover:opacity-90 disabled:opacity-40 text-white px-5 py-2.5 rounded-[5px] text-[14px] font-medium"
          data-testid="generate-btn"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={14} />}
          Générer le bundle
        </button>
      </div>

      {/* CURRENT BUNDLE PROGRESS + PREVIEW */}
      {selected && (
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-5 mb-8" data-testid="bundle-preview">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1">{selected.status}</div>
              <h2 className="font-display text-2xl text-[color:var(--gv-ink)]">{selected.theme}</h2>
              <div className="text-[12px] text-[color:var(--gv-ink)]/55 mt-1">Bundle {selected.bundle_id}</div>
            </div>
            <div className="flex gap-2">
              {selected.status === "done" && (
                <button onClick={() => publish(selected.bundle_id)} className="inline-flex items-center gap-1.5 bg-[color:var(--gv-ink)] text-white px-4 py-2 rounded-[5px] text-[13px] hover:opacity-90" data-testid="publish-btn">
                  <Upload size={13} /> Publier tout
                </button>
              )}
              <button onClick={() => del(selected.bundle_id)} className="inline-flex items-center gap-1.5 bg-white border border-red-300 text-red-600 px-4 py-2 rounded-[5px] text-[13px] hover:bg-red-50" data-testid="delete-btn">
                <Trash2 size={13} /> Supprimer
              </button>
            </div>
          </div>

          {/* Progress bar */}
          {selected.status !== "done" && selected.status !== "failed" && selected.status !== "published" && (
            <div className="mb-5">
              <div className="flex items-center gap-2 text-[13px] text-[color:var(--gv-ink)]/70 mb-2">
                <Loader2 size={14} className="animate-spin text-[color:var(--gv-pink)]" />
                {selected.step || "En attente…"}
                <span className="ml-auto text-[color:var(--gv-ink)]/45">{selected.progress}/{selected.total_steps}</span>
              </div>
              <div className="h-2 bg-[color:var(--gv-mist)] rounded-full overflow-hidden">
                <div className="h-full bg-[color:var(--gv-pink)] transition-all" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          )}

          {selected.status === "failed" && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px] text-[13px] mb-4 flex items-center gap-2">
              <XCircle size={16} /> Échec — {selected.error}
            </div>
          )}

          {selected.status === "published" && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded-[5px] text-[13px] mb-4 flex items-center gap-2">
              <CheckCircle2 size={16} /> Publié — {JSON.stringify(selected.published_counts || {})}
            </div>
          )}

          {(selected.status === "done" || selected.status === "published") && (
            <>
              <BundlePanel
                title="Fiche appellation"
                testId="panel-appellation"
                onRegen={() => regenElement("appellation")}
                onEdit={() => openEdit("appellation")}
                regenBusy={regenBusy === "appellation"}
              >
                <pre className="text-[12.5px] bg-[color:var(--gv-mist)] p-3 rounded-[5px] overflow-x-auto whitespace-pre-wrap">{JSON.stringify(selected.appellation, null, 2)}</pre>
              </BundlePanel>
              <BundlePanel
                title={`Cépages (${(selected.cepages || []).length})`}
                testId="panel-cepages"
                onRegen={() => regenElement("cepages")}
                onEdit={() => openEdit("cepages")}
                regenBusy={regenBusy === "cepages"}
              >
                <pre className="text-[12.5px] bg-[color:var(--gv-mist)] p-3 rounded-[5px] overflow-x-auto whitespace-pre-wrap">{JSON.stringify(selected.cepages, null, 2)}</pre>
              </BundlePanel>
              <BundlePanel
                title={`Producteurs (${(selected.producers || []).length})`}
                testId="panel-producers"
                onRegen={() => regenElement("producers")}
                onEdit={() => openEdit("producers")}
                regenBusy={regenBusy === "producers"}
              >
                <pre className="text-[12.5px] bg-[color:var(--gv-mist)] p-3 rounded-[5px] overflow-x-auto whitespace-pre-wrap">{JSON.stringify(selected.producers, null, 2)}</pre>
              </BundlePanel>
              <BundlePanel
                title={`Articles blog (${(selected.articles || []).length})`}
                testId="panel-articles"
                onRegen={() => regenElement("articles")}
                onEdit={() => openEdit("articles")}
                regenBusy={regenBusy === "articles"}
              >
                {(selected.articles || []).map((a, i) => (
                  <div key={i} className="mb-4 border border-[color:var(--gv-line)] rounded-[5px] p-3">
                    <div className="font-medium text-[14px] mb-1">{a.title}</div>
                    <div className="text-[12px] text-[color:var(--gv-ink)]/60 mb-2">{a.slug} · {a.category} · {a.read_minutes}min</div>
                    <div className="text-[13px] italic mb-2">{a.excerpt}</div>
                    <details><summary className="cursor-pointer text-[12px] text-[color:var(--gv-pink)]">Voir le contenu markdown</summary><pre className="text-[12px] bg-[color:var(--gv-mist)] p-2 rounded whitespace-pre-wrap mt-2">{a.body_markdown}</pre></details>
                  </div>
                ))}
              </BundlePanel>
              {selected.academy && (
                <BundlePanel
                  title="Cours Academy"
                  testId="panel-academy"
                  onRegen={() => regenElement("academy")}
                  onEdit={() => openEdit("academy")}
                  regenBusy={regenBusy === "academy"}
                >
                  <pre className="text-[12.5px] bg-[color:var(--gv-mist)] p-3 rounded-[5px] overflow-x-auto whitespace-pre-wrap">{JSON.stringify(selected.academy, null, 2)}</pre>
                </BundlePanel>
              )}
              {selected.translations && (
                <BundlePanel
                  title={`Traductions (${Object.keys(selected.translations).length})`}
                  testId="panel-translations"
                  onRegen={() => regenElement("translations")}
                  onEdit={() => openEdit("translations")}
                  regenBusy={regenBusy === "translations"}
                >
                  <pre className="text-[12.5px] bg-[color:var(--gv-mist)] p-3 rounded-[5px] overflow-x-auto whitespace-pre-wrap max-h-96">{JSON.stringify(selected.translations, null, 2)}</pre>
                </BundlePanel>
              )}
              {selected.newsletter && !selected.newsletter.error && (
                <BundlePanel
                  title={`Newsletter 4 langues (FR/EN/ES/PT)`}
                  testId="panel-newsletter"
                  onRegen={() => setNewsletterLangModal({ langs: new Set(["fr","en","es","pt"]) })}
                  onEdit={() => openEdit("newsletter")}
                  regenBusy={regenBusy === "newsletter"}
                >
                  {["fr", "en", "es", "pt"].map((lg) => {
                    const b = selected.newsletter[lg] || {};
                    return (
                      <div key={lg} className="mb-3 border border-[color:var(--gv-line)] rounded-[5px] p-3">
                        <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1">{lg}</div>
                        <div className="font-medium text-[14px] mb-1">{b.subject}</div>
                        <div className="text-[12.5px] text-[color:var(--gv-ink)]/75 italic mb-2">{b.intro}</div>
                        <div className="text-[12.5px] text-[color:var(--gv-ink)]/85 mb-2"><strong>Terroir — </strong>{b.terroir}</div>
                        <div className="text-[12.5px] text-[color:var(--gv-ink)]/75">{b.outro}</div>
                      </div>
                    );
                  })}
                  {selected.newsletter_send_at && (
                    <div className="text-[12px] text-[color:var(--gv-ink)]/60 mt-2">
                      Envoi programmé : {new Date(selected.newsletter_send_at).toLocaleString()}
                    </div>
                  )}
                </BundlePanel>
              )}
              {selected.podcast && !selected.podcast.error && (
                <BundlePanel
                  title={`Podcast scripts 4 langues (FR/EN/ES/PT)`}
                  testId="panel-podcast"
                  onRegen={() => regenElement("podcast")}
                  onEdit={() => openEdit("podcast")}
                  regenBusy={regenBusy === "podcast"}
                >
                  {["fr", "en", "es", "pt"].map((lg) => {
                    const p = selected.podcast[lg] || {};
                    return (
                      <div key={lg} className="mb-3 border border-[color:var(--gv-line)] rounded-[5px] p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)]">{lg}</div>
                          <div className="text-[11px] text-[color:var(--gv-ink)]/55">{p.duration_sec ? `${Math.round(p.duration_sec / 60)} min` : ""}</div>
                        </div>
                        <div className="font-medium text-[14px] mb-1">{p.title}</div>
                        <div className="text-[12.5px] text-[color:var(--gv-ink)]/75 italic mb-2">{p.intro}</div>
                        <details>
                          <summary className="cursor-pointer text-[12px] text-[color:var(--gv-pink)]">Script complet (markdown)</summary>
                          <pre className="text-[12px] bg-[color:var(--gv-mist)] p-2 rounded whitespace-pre-wrap mt-2">{p.body_markdown}</pre>
                        </details>
                        <div className="text-[12.5px] text-[color:var(--gv-ink)]/75 mt-2">{p.outro}</div>
                      </div>
                    );
                  })}
                </BundlePanel>
              )}
              {selected.icp_prospecting && !selected.icp_prospecting.error && (
                <BundlePanel
                  title={`Prospection B2B · ${(selected.icp_prospecting.items || []).length} ICP`}
                  testId="panel-icp"
                  onRegen={() => regenElement("icp_prospecting")}
                  onEdit={() => openEdit("icp_prospecting")}
                  regenBusy={regenBusy === "icp_prospecting"}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(selected.icp_prospecting.items || []).map((it) => (
                      <div key={it.icp} className="border border-[color:var(--gv-line)] rounded-[5px] p-3 bg-white">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="font-display text-[14px] text-[color:var(--gv-ink)]">{it.label}</div>
                          <span className="text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[color:var(--gv-pink)] text-[color:var(--gv-pink)]">
                            {`geoVINUM ${it.product}`}
                          </span>
                        </div>
                        {it.angle && <div className="text-[12.5px] text-[color:var(--gv-ink)]/80 italic mb-1.5">{it.angle}</div>}
                        {it.pitch && <div className="text-[12.5px] text-[color:var(--gv-ink)]/70 mb-1.5">{it.pitch}</div>}
                        {it.cta && (
                          <div className="text-[11.5px] inline-flex items-center gap-1 text-[color:var(--gv-pink)] font-medium">→ {it.cta}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </BundlePanel>
              )}
              {selected.fiche_translations && (
                <BundlePanel
                  title={`Traductions fiches atlas (EN/ES/PT)`}
                  testId="panel-fiche-translations"
                  onRegen={() => regenElement("fiche_translations")}
                  onEdit={() => openEdit("fiche_translations")}
                  regenBusy={regenBusy === "fiche_translations"}
                >
                  <pre className="text-[12.5px] bg-[color:var(--gv-mist)] p-3 rounded-[5px] overflow-x-auto whitespace-pre-wrap max-h-96">{JSON.stringify(selected.fiche_translations, null, 2)}</pre>
                </BundlePanel>
              )}
              {selected.print_product && (
                <BundlePanel title="Carte associée" testId="panel-print">
                  <div className="flex items-start gap-4">
                    {selected.print_product.preview_url && (
                      <img src={selected.print_product.preview_url} alt="" className="w-24 h-auto border border-[color:var(--gv-line)] rounded" />
                    )}
                    <div>
                      <div className="font-medium">{selected.print_product.title}</div>
                      <div className="text-[12px] text-[color:var(--gv-ink)]/55 mt-1">{selected.print_product.product_id}</div>
                    </div>
                  </div>
                </BundlePanel>
              )}
            </>
          )}
        </div>
      )}

      {/* HISTORY */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-5" data-testid="bundle-history">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-[color:var(--gv-ink)]">Historique</h2>
          <button onClick={refreshList} className="text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-pink)]" data-testid="refresh-list">
            <RefreshCw size={14} />
          </button>
        </div>
        {bundles.length === 0 ? (
          <div className="text-[13px] text-[color:var(--gv-ink)]/55">Aucun bundle encore. Lance ta première génération ci-dessus.</div>
        ) : (
          <ul className="divide-y divide-[color:var(--gv-line)]">
            {bundles.map((b) => (
              <li key={b.bundle_id} className="py-3 flex items-center gap-3">
                <button
                  onClick={() => setSelected(b)}
                  className="flex-1 text-left min-w-0"
                  data-testid={`bundle-item-${b.bundle_id}`}
                >
                  <div className="font-medium text-[14px] truncate">{b.theme}</div>
                  <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 mt-0.5">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest mr-2 ${
                      b.status === "done" ? "bg-emerald-100 text-emerald-700" :
                      b.status === "failed" ? "bg-red-100 text-red-700" :
                      b.status === "published" ? "bg-blue-100 text-blue-700" :
                      "bg-yellow-100 text-yellow-700"
                    }`}>{b.status}</span>
                    {b.created_at?.slice(0, 16).replace("T", " ")}
                  </div>
                </button>
                <button onClick={() => del(b.bundle_id)} className="text-red-500/70 hover:text-red-700" data-testid={`delete-${b.bundle_id}`}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* EDIT ELEMENT MODAL — raw JSON editor */}
      {editingEl && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="edit-modal">
          <div className="bg-white rounded-[6px] p-6 max-w-3xl w-full shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xl">Modifier · {editingEl.element}</h3>
              <button onClick={() => setEditingEl(null)} className="text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]">
                <X size={18} />
              </button>
            </div>
            <p className="text-[12.5px] text-[color:var(--gv-ink)]/60 mb-3">
              Édite le JSON brut. Attention à bien conserver la structure attendue (clés identiques).
            </p>
            <textarea
              value={editingEl.text}
              onChange={(e) => setEditingEl({ ...editingEl, text: e.target.value })}
              className="flex-1 w-full font-mono text-[12.5px] border border-[color:var(--gv-line)] rounded-[5px] p-3 min-h-[400px] focus:border-[color:var(--gv-pink)] focus:outline-none"
              spellCheck="false"
              data-testid="edit-textarea"
            />
            <div className="flex gap-2 justify-end mt-3">
              <button onClick={() => setEditingEl(null)} className="px-4 py-2 rounded-[5px] border border-[color:var(--gv-line)] text-[13px] hover:bg-[color:var(--gv-mist)]">Annuler</button>
              <button onClick={saveEdit} disabled={editSaving} className="inline-flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] hover:opacity-90 disabled:opacity-40" data-testid="edit-save">
                {editSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEWSLETTER REGEN LANG MODAL — pick which languages to regenerate */}
      {newsletterLangModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="newsletter-lang-modal">
          <div className="bg-white rounded-[6px] p-6 max-w-sm w-full shadow-xl">
            <h3 className="font-display text-xl mb-3">Régénérer la newsletter</h3>
            <p className="text-[12.5px] text-[color:var(--gv-ink)]/60 mb-4">
              Choisis la ou les langues à régénérer. La régénération se fait par bloc complet (sujet, intro, terroir, outro).
            </p>
            <div className="space-y-2 mb-4">
              {["fr", "en", "es", "pt"].map((lg) => (
                <label key={lg} className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newsletterLangModal.langs.has(lg)}
                    onChange={(e) => {
                      const n = new Set(newsletterLangModal.langs);
                      if (e.target.checked) n.add(lg); else n.delete(lg);
                      setNewsletterLangModal({ langs: n });
                    }}
                    className="accent-[color:var(--gv-pink)]"
                    data-testid={`nl-lang-${lg}`}
                  />
                  <span className="uppercase tracking-wider text-[11.5px] text-[color:var(--gv-pink)]">{lg}</span>
                  <span className="text-[color:var(--gv-ink)]/70">
                    {{fr:"Français", en:"English", es:"Español", pt:"Português"}[lg]}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setNewsletterLangModal(null)} className="px-4 py-2 rounded-[5px] border border-[color:var(--gv-line)] text-[13px] hover:bg-[color:var(--gv-mist)]">
                Annuler
              </button>
              <button
                onClick={async () => {
                  const langs = Array.from(newsletterLangModal.langs);
                  setNewsletterLangModal(null);
                  await regenElement("newsletter", { languages: langs });
                }}
                disabled={newsletterLangModal.langs.size === 0}
                className="px-4 py-2 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] hover:opacity-90 disabled:opacity-40"
                data-testid="newsletter-lang-confirm"
              >
                Régénérer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PUBLISH MODAL — datetime picker for newsletter send */}
      {publishModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="publish-modal">
          <div className="bg-white rounded-[6px] p-6 max-w-md w-full shadow-xl">
            <h3 className="font-display text-xl mb-3">Publier le bundle</h3>
            <p className="text-[13px] text-[color:var(--gv-ink)]/70 mb-4">
              Articles (FR + EN/ES/PT), cépages, producteurs, academy brouillon seront créés.
              La newsletter 4 langues sera programmée.
            </p>
            <label className="flex items-center gap-2 text-[13px] mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={publishModal.sendNewsletter}
                onChange={(e) => setPublishModal({ ...publishModal, sendNewsletter: e.target.checked })}
                className="accent-[color:var(--gv-pink)]"
                data-testid="toggle-send-newsletter"
              />
              Envoyer la newsletter aux abonnés
            </label>
            {publishModal.sendNewsletter && (
              <div className="mb-5">
                <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1.5">Date & heure d'envoi</label>
                <input
                  type="datetime-local"
                  value={publishModal.sendAt}
                  onChange={(e) => setPublishModal({ ...publishModal, sendAt: e.target.value })}
                  className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-[14px] focus:border-[color:var(--gv-pink)] focus:outline-none"
                  data-testid="send-at-input"
                />
                <div className="text-[11px] text-[color:var(--gv-ink)]/50 mt-1">
                  La newsletter sera envoyée à chaque abonné dans sa langue (FR/EN/ES/PT).
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setPublishModal(null)}
                className="px-4 py-2 rounded-[5px] border border-[color:var(--gv-line)] text-[13px] hover:bg-[color:var(--gv-mist)]"
                data-testid="publish-cancel"
              >
                Annuler
              </button>
              <button
                onClick={confirmPublish}
                className="px-4 py-2 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] hover:opacity-90"
                data-testid="publish-confirm"
              >
                Publier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BundlePanel = ({ title, testId, children, onRegen, onEdit, regenBusy, actions }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-[color:var(--gv-line)] rounded-[5px] mb-3" data-testid={testId}>
      <div className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-[color:var(--gv-mist)]/30 transition-colors">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 flex-1 text-left">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="font-medium text-[14px]">{title}</span>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {actions}
          {onRegen && (
            <button
              onClick={onRegen}
              disabled={regenBusy}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11.5px] border border-[color:var(--gv-line)] rounded hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] disabled:opacity-40 transition-colors"
              data-testid={`${testId}-regen`}
              title="Régénérer avec DeepSeek"
            >
              {regenBusy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Régénérer
            </button>
          )}
          {onEdit && (
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11.5px] border border-[color:var(--gv-line)] rounded hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] transition-colors"
              data-testid={`${testId}-edit`}
              title="Modifier le JSON"
            >
              <Edit3 size={11} /> Modifier
            </button>
          )}
        </div>
      </div>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

export default AdminMonthlyThemePage;
